import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

/**
 * Interface pour les données client
 */
export interface SendToClientData {
  id?: number;
  user_id?: number;
  client_type: 'particulier' | 'societe';
  email: string;
  adresse: string;
  telephone: string;
  
  // Champs pour particulier
  civilite?: 'Mr' | 'Mme' | null;
  nom?: string | null;
  prenom?: string | null;
  
  // Champs pour société
  siren?: string | null;
  raison_sociale?: string | null;
  tva_intra?: string | null;
  
  // Champs communs
  profession?: string | null;
  show_in_directory: boolean;
  status: 'active' | 'inactive';
  
  // Timestamps
  created_at?: string;
  updated_at?: string;
  firstname?: string;
  name?: string;
  lastname?: string;
}
/**
 * Interface pour la réponse standard de l'API
 */
export interface SendToClientResponse {
  success: boolean;
  message: string;
  data?: SendToClientData | SendToClientData[];
}

/**
 * Interface pour l'envoi du solde
 */
export interface SendSoldePayload {
  clientId: number;
  amount: number;
  currency?: string;
}

/**
 * Service pour gérer les clients et envoyer les soldes
 */
class SendToClientService {
  /**
   * Récupère les headers d'authentification avec le token Bearer
   */
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      Authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json'
    };
  }

  /**
   * GET /api/send-to-client/clients
   * Récupère tous les clients pour l'utilisateur connecté
   */
  async getAllClients(): Promise<SendToClientData[]> {
    try {
      const response = await axios.get<SendToClientResponse>(
        `${API_BASE_URL}/send-to-client/clients`,
        { headers: this.getAuthHeaders() }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur lors de la récupération des clients');
      }

      const data = response.data.data;
      if (Array.isArray(data)) {
        return data;
      }
      return [];
    } catch (error) {
      console.error('Erreur getAllClients:', error);
      throw error;
    }
  }

  /**
   * POST /api/send-to-client/clients
   * Crée un nouveau client
   */
  async createClient(
    clientData: Omit<SendToClientData, 'id' | 'created_at' | 'updated_at'>
  ): Promise<SendToClientData> {
    try {
      const response = await axios.post<SendToClientResponse>(
        `${API_BASE_URL}/send-to-client/clients`,
        clientData,
        { headers: this.getAuthHeaders() }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur lors de la création du client');
      }

      return response.data.data as SendToClientData;
    } catch (error) {
      console.error('Erreur createClient:', error);
      throw error;
    }
  }

  /**
   * PUT /api/send-to-client/clients/:id
   * Met à jour un client existant
   */
  async updateClient(
    clientId: number,
    clientData: Partial<SendToClientData>
  ): Promise<SendToClientData> {
    try {
      const response = await axios.put<SendToClientResponse>(
        `${API_BASE_URL}/send-to-client/clients/${clientId}`,
        clientData,
        { headers: this.getAuthHeaders() }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur lors de la mise à jour du client');
      }

      return response.data.data as SendToClientData;
    } catch (error) {
      console.error('Erreur updateClient:', error);
      throw error;
    }
  }

  /**
   * DELETE /api/send-to-client/clients/:id
   * Supprime un client
   */
  async deleteClient(clientId: number): Promise<void> {
    try {
      const response = await axios.delete<SendToClientResponse>(
        `${API_BASE_URL}/send-to-client/clients/${clientId}`,
        { headers: this.getAuthHeaders() }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur lors de la suppression du client');
      }
    } catch (error) {
      console.error('Erreur deleteClient:', error);
      throw error;
    }
  }

  /**
   * POST /api/send-to-client/send-solde
   * Envoie le solde au client (remises enlevées)
   * Génère un code de remise unique
   */
  async sendSoldeToClient(clientId: number, amount: number, discountPercentage: number = 0): Promise<{ success: boolean; message: string; code?: string }> {
    try {
      const response = await axios.post<any>(
        `${API_BASE_URL}/send-to-client/send-solde`,
        { clientId, amount, discountPercentage },
        { headers: this.getAuthHeaders() }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur lors de l\'envoi du solde');
      }

      return {
        success: response.data.success,
        message: response.data.message || 'Solde envoyé avec succès',
        code: response.data.data?.code // Code de remise généré
      };
    } catch (error) {
      console.error('Erreur sendSoldeToClient:', error);
      throw error;
    }
  }

  /**
   * GET /api/discount-codes/my-codes
   * Récupère les codes de remise créés par l'utilisateur (codes envoyés aux clients)
   */
  async getMyDiscountCodes(): Promise<any[]> {
    try {
      const response = await axios.get<any>(
        `${API_BASE_URL}/discount-codes/my-codes`,
        { headers: this.getAuthHeaders() }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur lors de la récupération des codes');
      }

      return response.data.data || [];
    } catch (error) {
      console.error('Erreur getMyDiscountCodes:', error);
      throw error;
    }
  }

  /**
   * POST /api/discount-codes/use
   * Utilise un code de remise (ajoute le montant au soldeRemise)
   */
  async applyDiscountCode(code: string): Promise<{ success: boolean; message: string; newBalance?: number }> {
    try {
      const response = await axios.post<any>(
        `${API_BASE_URL}/discount-codes/use`,
        { code },
        { headers: this.getAuthHeaders() }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur lors de l\'utilisation du code');
      }

      return {
        success: response.data.success,
        message: response.data.message,
        newBalance: response.data.data?.newBalance
      };
    } catch (error) {
      console.error('Erreur applyDiscountCode:', error);
      throw error;
    }
  }

  /**
   * GET /api/discount-codes/validate/:code
   * Valide un code de remise sans l'utiliser
   */
    async validateDiscountCode(
    code: string
    ): Promise<{
    isValid: boolean;
    discountPercentage?: number;
    message: string;
    }> {
    try {
        const response = await axios.get(
        `${API_BASE_URL}/discount-codes/validate/${code}`,
        { headers: this.getAuthHeaders() }
        );

        const data = response.data?.data;

        return {
        isValid: data?.isValid === true,
        discountPercentage: Number(data?.discountPercentage),
        message: data?.message || 'Code invalide'
        };
    } catch (error) {
        console.error('Erreur validateDiscountCode:', error);
        return {
        isValid: false,
        message: 'Erreur lors de la validation du code'
        };
    }
    }

}

// Exporter une instance singleton du service
const sendToClientService = new SendToClientService();
export default sendToClientService;
